import type {Options} from 'highcharts';
import type {TableSpec} from './table/model';

export type MemberRole = 'total' | 'missing';
export type Metric = 'count' | 'share' | 'value' | 'distribution' | 'stats';

export interface CubeMember {
    key: string;
    label: string;
    color?: string;
    role?: MemberRole;
    from?: string;
    to?: string;
    /** [lng, lat] positions of the option; one is a point, two or more a polygon */
    geo?: [number, number][];
}

export interface CubeDimension {
    id: string;
    label: string;
    kind: string;
    members: CubeMember[];
}

export interface CubeMeasure {
    id: string;
    label: string;
    metric: Metric;
    unit?: string;
    precision: number;
    color?: string;
    group?: string;
    scale?: {min: number; max: number};
}

export interface CubeCellValue {
    value: number | null;
    /** Respondents, or the sum of their weights on a weighted project */
    count: number;
    /** Respondents behind count; set on a weighted project only */
    n?: number;
    base?: number;
    baseN?: number;
    previous?: number | null;
    previousCount?: number;
    previousN?: number;
    lowBase?: boolean;
    stats?: [number, number, number, number, number];
}

export interface CubeCell {
    coords: number[];
    values: Record<string, CubeCellValue>;
}

export interface CubeTerm {
    from: string;
    to: string;
    label: string;
}

export interface CubeMeta {
    term: CubeTerm;
    previousTerm: CubeTerm | null;
    lowBase: {threshold: number; mode: 'mark' | 'filter' | 'hide' | 'disabled'} | null;
}

export interface Cube {
    dimensions: CubeDimension[];
    measures: CubeMeasure[];
    cells: CubeCell[];
    meta: CubeMeta;
}

export interface SegmentControlOption {
    value: string;
    label: string;
}

export interface SegmentControlCondition {
    /** `type:target`, the key of the condition in the viewer's selection */
    id: string;
    type: 'classification' | 'variable' | 'choices';
    target: string;
    label: string;
    /** Only those within the viewer's data restriction */
    options: SegmentControlOption[];
    /** `all`: no constraint from the condition (everybody within the restriction); a list narrows, `[]` selects nobody */
    selected: SegmentSelectionValues;
}

export type SegmentSelectionValues = string[] | typeof ALL_SELECTED;

/** query.v1 segmentCondition values (and the viewer's selection) for "no constraint" */
export const ALL_SELECTED = 'all';

/** The segments the viewer can change (server SegmentControlsBuilder) */
export interface SegmentControls {
    /** Name of the own-filter input that carries the selection */
    field: string;
    dimension: string;
    segments: {
        key: string;
        /** Fixed label of the segment; null when the member label is the summary of the selection */
        label: string | null;
        conditions: SegmentControlCondition[];
    }[];
}

export interface RenderSpec {
    mark: string;
    options: Record<string, any>;
    encoding: Record<string, string | string[]>;
    highcharts: Record<string, any>;
    /** Present only with segments the viewer can change */
    controls?: SegmentControls;
}

/** Order of CubeCellValue.stats */
export const STATS_KEYS = ['min', 'q1', 'median', 'q3', 'max'] as const;
export type StatsKey = typeof STATS_KEYS[number];

export const LABEL_KEYS = ['min', 'q1', 'median', 'q3', 'max', 'previous', 'current', 'noData', 'mapSummary', 'mapZoomHint', 'mapNoLocation', 'rank', 'rankChange', 'lowBaseExcluded',
    'comparisonValue', 'comparisonReference', 'comparisonVersusReference', 'againstPrevious', 'segmentPickerTitle', 'selectAll', 'deselectAll', 'apply',
    'cancel', 'none_selected_text', 'select_all_text', 'count_selected_text', 'comparisonVersusSegment',
    'comparisonVersusReferenceShort'] as const;

export interface RenderContext {
    dark: boolean;
    palette: string[];
    locale: string;
    /** Translated texts; the key itself is the fallback */
    labels?: Partial<Record<typeof LABEL_KEYS[number], string>>;
    /**
     * Changes a viewer control (an own-filter field) and refetches; absent where the viewer cannot change anything.
     * `update` gets the field's current value, which a quick second change must build on rather than on the rendered data.
     */
    setControl?: (field: string, update: (current: string | null) => string) => void;
}

/** Lifecycle of a mark that owns resources the host has to keep in step (a map), which Highcharts handles itself. */
export interface MarkHandle {
    resize?(): void;
    destroy?(): void;
}

export type MarkOutput =
    | {type: 'highcharts'; options: Options}
    /** `table`: the model a table mark rendered, so CSV exports exactly that */
    | {type: 'html'; render(el: HTMLElement): void | MarkHandle; table?: TableSpec};

export type MarkRenderer = (cube: Cube, spec: RenderSpec, ctx: RenderContext) => MarkOutput;

export interface ChartV2Data {
    cube: Cube;
    spec: RenderSpec;
}

/** Pseudo-dimension in encoding: the measures themselves act as members of a channel. */
export const MEASURES_CHANNEL = '$measures';
